import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import OfferCard from "@/components/base/OfferCard";
import CountdownTimer from "./components/CountdownTimer";
import { useAdminProducts } from "@/hooks/useAdminStore";
import { useAdminCategories } from "@/hooks/useAdminCategories";
import { useAdminMarketplaces } from "@/hooks/useAdminMarketplaces";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getMillisecondsUntilEndOfDayInAppTimeZone } from "@/utils/date";
import { usePageSeo } from "@/hooks/usePageSeo";

type SortOption = "maior-desconto" | "menor-preco" | "maior-preco" | "mais-vendidos";

const SORT_LABELS: Record<SortOption, string> = {
  "maior-desconto": "Maior Desconto",
  "menor-preco": "Menor Preço",
  "maior-preco": "Maior Preço",
  "mais-vendidos": "Mais Vendidos",
};

export default function DealsPage() {
  const { products } = useAdminProducts();
  const { categories } = useAdminCategories();
  const { activeMarketplaceOptions } = useAdminMarketplaces();
  const { settings } = useSiteSettings();
  const [activeMarketplace, setActiveMarketplace] = useState("Todos");
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [sort, setSort] = useState<SortOption>("maior-desconto");
  const [onlyFlash, setOnlyFlash] = useState(false);
  const [onlyFreeShipping, setOnlyFreeShipping] = useState(false);

  const midnight = useMemo(
    () => Date.now() + getMillisecondsUntilEndOfDayInAppTimeZone(),
    []
  );

  const deals = useMemo(
    () => products
      .filter((product) => product.showInDailyDeals)
      .map((product) => {
        const sold = Math.max(product.reviews, 0);
        return {
          ...product,
          sold,
          stock: Math.max(sold + 20, 100),
          isFlash: product.badge === "Oferta Relâmpago",
          endsAt: midnight,
        };
      }),
    [midnight, products]
  );

  usePageSeo({
    title: `Ofertas do Dia | ${settings.siteName}`,
    description: `Confira ${deals.length.toLocaleString("pt-BR")} ofertas ativas com descontos, reviews e links de afiliado atualizados em tempo real no ${settings.siteName}.`,
    canonicalPath: "/ofertas",
    keywords: settings.seoKeywords,
    image: settings.dealsHeroImageUrl,
    siteName: settings.siteName,
    ogType: "website",
  });

  const marketplaces = useMemo(
    () => ["Todos", ...Array.from(new Set([...activeMarketplaceOptions, ...deals.map((deal) => deal.marketplace)].filter(Boolean)))],
    [activeMarketplaceOptions, deals]
  );

  const dealCategories = useMemo(
    () => ["Todos", ...Array.from(new Set([...categories.map((category) => category.name), ...deals.map((deal) => deal.category)].filter(Boolean)))],
    [categories, deals]
  );

  const flashDeals = deals.filter((deal) => deal.isFlash);

  const filtered = useMemo(() => {
    let list = [...deals];
    if (activeMarketplace !== "Todos") list = list.filter((deal) => deal.marketplace === activeMarketplace);
    if (activeCategory !== "Todos") list = list.filter((deal) => deal.category === activeCategory);
    if (onlyFlash) list = list.filter((deal) => deal.isFlash);
    if (onlyFreeShipping) list = list.filter((deal) => deal.freeShipping);

    list.sort((a, b) => {
      if (sort === "maior-desconto") return b.discount - a.discount;
      if (sort === "menor-preco") return a.price - b.price;
      if (sort === "maior-preco") return b.price - a.price;
      if (sort === "mais-vendidos") return b.sold - a.sold;
      return 0;
    });
    return list;
  }, [activeMarketplace, activeCategory, sort, onlyFlash, onlyFreeShipping, deals]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-16 relative overflow-hidden">
        <div className="relative h-[212px] md:h-[212px]">
          <img
            src={settings.dealsHeroImageUrl}
            alt="Ofertas do Dia"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-transparent"></div>

          <div className="relative z-10 flex h-[212px] items-center">
            <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
              <div className="max-w-xl">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-red-500 text-white text-xs font-black px-3 py-1 rounded-full flex items-center gap-1 animate-pulse">
                    <i className="ri-flashlight-fill"></i>
                    AO VIVO
                  </span>
                  <span className="text-white/70 text-sm">{deals.length} produtos habilitados hoje</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-black text-white mb-2 leading-tight">
                  Ofertas do Dia
                </h1>
                <p className="text-white/80 text-sm mb-4 max-w-lg line-clamp-2">
                  Os produtos habilitados no cadastro e no painel administrativo aparecem aqui automaticamente, mantendo o layout atual do site.
                </p>

                <div className="flex items-center gap-3">
                  <span className="text-white/60 text-xs font-semibold">Ofertas renovam em:</span>
                  <CountdownTimer endsAt={midnight} size="lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {flashDeals.length > 0 && (
        <div className="bg-gradient-to-r from-red-600 to-orange-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4 overflow-x-auto">
            <span className="text-white font-black text-sm whitespace-nowrap flex items-center gap-1 flex-shrink-0">
              <i className="ri-flashlight-fill"></i>
              RELÂMPAGO:
            </span>
            {flashDeals.map((deal) => (
              <a
                key={deal.id}
                href={deal.affiliateLink}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1.5 whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <span className="text-white text-xs font-semibold">{deal.name.split(" ").slice(0, 4).join(" ")}</span>
                <span className="bg-white text-red-600 text-xs font-black px-2 py-0.5 rounded-full">-{deal.discount}%</span>
              </a>
            ))}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="w-full max-w-3xl mx-auto h-[90px] rounded-xl border border-dashed border-gray-200 bg-white flex items-center justify-center mb-8">
          <span className="text-gray-300 text-xs font-semibold">Anúncio 728×90</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
          <aside className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-filter-3-line text-orange-500"></i>
                </div>
                Filtros
              </h3>

              <div className="mb-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Marketplace</p>
                <div className="space-y-1">
                  {marketplaces.map((marketplace) => (
                    <button
                      key={marketplace}
                      onClick={() => setActiveMarketplace(marketplace)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer flex items-center justify-between ${
                        activeMarketplace === marketplace
                          ? "bg-orange-500 text-white font-semibold"
                          : "text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                      }`}
                    >
                      <span>{marketplace}</span>
                      {activeMarketplace === marketplace && <i className="ri-check-line text-xs"></i>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Categoria</p>
                <div className="space-y-1">
                  {dealCategories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer flex items-center justify-between ${
                        activeCategory === category
                          ? "bg-orange-500 text-white font-semibold"
                          : "text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                      }`}
                    >
                      <span>{category}</span>
                      {activeCategory === category && <i className="ri-check-line text-xs"></i>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3 pt-3 border-t border-gray-100">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-600 font-medium">Só Relâmpago</span>
                  <button
                    onClick={() => setOnlyFlash(!onlyFlash)}
                    className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${onlyFlash ? "bg-orange-500" : "bg-gray-200"}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${onlyFlash ? "left-5" : "left-0.5"}`} />
                  </button>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-600 font-medium">Frete Grátis</span>
                  <button
                    onClick={() => setOnlyFreeShipping(!onlyFreeShipping)}
                    className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${onlyFreeShipping ? "bg-orange-500" : "bg-gray-200"}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${onlyFreeShipping ? "left-5" : "left-0.5"}`} />
                  </button>
                </label>
              </div>

              {(activeMarketplace !== "Todos" || activeCategory !== "Todos" || onlyFlash || onlyFreeShipping) && (
                <button
                  onClick={() => {
                    setActiveMarketplace("Todos");
                    setActiveCategory("Todos");
                    setOnlyFlash(false);
                    setOnlyFreeShipping(false);
                  }}
                  className="mt-4 w-full py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer"
                >
                  Limpar filtros
                </button>
              )}
            </div>

            <div className="rounded-2xl border border-dashed border-gray-200 bg-white flex flex-col items-center justify-center h-[250px]">
              <div className="w-8 h-8 flex items-center justify-center mb-2">
                <i className="ri-advertisement-line text-gray-300 text-2xl"></i>
              </div>
              <span className="text-gray-300 text-xs font-semibold">Anúncio 300×250</span>
            </div>
          </aside>

          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="font-black text-gray-900 text-lg">
                  {activeMarketplace !== "Todos" ? activeMarketplace : "Todas as Ofertas"}
                  {activeCategory !== "Todos" && ` · ${activeCategory}`}
                </h2>
                <p className="text-gray-400 text-sm">{filtered.length} ofertas encontradas</p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-gray-500 whitespace-nowrap">Ordenar por:</span>
                <div className="flex flex-wrap gap-1">
                  {(Object.keys(SORT_LABELS) as SortOption[]).map((key) => (
                    <button
                      key={key}
                      onClick={() => setSort(key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        sort === key
                          ? "bg-orange-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                      }`}
                    >
                      {SORT_LABELS[key]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-gray-300"></i>
                </div>
                <p className="text-gray-500 font-semibold">Nenhuma oferta encontrada</p>
                <p className="text-gray-400 text-sm mt-1">Tente remover alguns filtros para ver mais produtos.</p>
              </div>
            ) : (
              <div className="flex flex-wrap justify-center gap-4">
                {filtered.map((deal) => (
                  <OfferCard
                    key={deal.slug}
                    item={{
                      slug: deal.slug,
                      image: deal.image,
                      name: deal.name,
                      price: deal.price,
                      originalPrice: deal.originalPrice,
                      discount: deal.discount,
                      marketplace: deal.marketplace,
                      freeShipping: deal.freeShipping,
                      fulfillment: deal.fulfillment,
                      officialStoreName: deal.officialStoreName,
                      affiliateLink: deal.affiliateLink,
                      isFlash: deal.isFlash,
                      category: deal.category,
                      categorySlug: deal.categorySlug,
                      subcategory: deal.subcategory,
                      subcategorySlug: deal.subcategorySlug,
                    }}
                    extra={
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{deal.sold.toLocaleString("pt-BR")} vendidos</span>
                          <span>{deal.stock.toLocaleString("pt-BR")} em estoque</span>
                        </div>
                        <CountdownTimer endsAt={deal.endsAt} size="sm" />
                      </div>
                    }
                  />
                ))}
              </div>
            )}

            <div className="mt-10 bg-white rounded-2xl border border-dashed border-gray-200 h-[120px] flex flex-col items-center justify-center">
              <div className="w-8 h-8 flex items-center justify-center mb-2">
                <i className="ri-advertisement-line text-gray-300 text-2xl"></i>
              </div>
              <span className="text-gray-300 text-xs font-semibold">Anúncio Responsivo</span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}